<template>
  <section class="commit-list-item">
    <div class="commit-list-item__info">
      <div class="commit-message">
        <router-link
          v-if="hasReactiveLinks"
          tag="a"
          class="commit-message__content"
          :to="{
            path: `/project/${$route?.params?.alias}/${$route?.params?.projectAlias}/commit/${commit?.hash}`
          }"
        >
          {{ commit.shortMessage }}
        </router-link>
        <a
          class="commit-message__content"
          v-else
          :href="`/project/${$route?.params?.alias}/${$route?.params?.projectAlias}/commit/${commit?.hash}`"
        >
          {{ commit.shortMessage }}
        </a>
      </div>
      <div class="commit-user">
        <div class="mr-1">
          <img
            height="20px"
            width="20px"
            :src="commit.user ? commit.user.avatar : commit.authorIdent.avatar"
            class="rounded-circle"
          />
        </div>
        <div class="f6 text-gray min-width-0">
          <a
            :href="'/user/' + commit.user.username"
            class="text-dark"
            v-if="commit.user"
          >
            {{ commit.user.fullName }}
          </a>
          <template v-else>
            <b>{{ commit.authorIdent.name }}</b>
            <br />
            {{ commit.authorIdent.emailAddress }}
          </template>
          <small class="text-muted ml-1">
            {{ commit.createdAt.substr(11, 20).slice(0, 8) }}
          </small>
        </div>
      </div>
    </div>
    <div class="commit-list-item__actions">
      <button
        v-if="commit?.verificationResult"
        type="button"
        class="d-flex align-items-master px-2 rounded bg-white"
        :style="gpgKeyInfo.elemStyle"
        :data-target="'commitGpgInfoModalWindow-' + commit?.hash"
      >
        <span class="d-none d-md-inline-block mr-1">
          {{ gpgKeyInfo.text }}
        </span>
        <span class="d-md-none">
          <i
            class="gf-icon"
            :class="gpgKeyInfo.icon"
          ></i>
        </span>
      </button>
      <gf-custom-tooltip v-if="canCreateTag">
        <template #element>
          <a
            :href="`/project/${$route?.params?.alias}/${$route?.params?.projectAlias}/tag/create?commitId=${commit?.hash}`"
            class="d-none d-sm-inline-flex btn btn-sm btn-outline-primary"
          >
            <i class="gf-icon gf-icon-tag"></i>
          </a>
        </template>
        <template #content>
          <span class="text-truncate">Создать тег</span>
        </template>
      </gf-custom-tooltip>
      <gf-custom-tooltip v-if="canStartContainer">
        <template #element>
          <button
            class="d-none d-sm-inline-flex btn btn-sm btn-outline-primary"
            @click="startCommitPipeline(commit.hash)"
          >
            <i class="gf-icon gf-icon-rocket"></i>
          </button>
        </template>
        <template #content>
          <span class="text-truncate">Запустить конвейер</span>
        </template>
      </gf-custom-tooltip>

      <gf-custom-tooltip v-if="getActionAvailabilityFlag(ACTION_ENUM.PUSH) && !isTagsDisplaying">
        <template #element>
          <button
            class="d-none d-sm-inline-flex btn btn-sm btn-outline-primary"
            @click="openRevertCommitModal"
          >
            <i class="gf-icon gf-icon-undo"></i>
          </button>
        </template>
        <template #content>
          <span class="text-truncate">Отменить коммит</span>
        </template>
      </gf-custom-tooltip>
      <gf-copy-commit-hash
        class="d-none d-sm-inline-flex"
        :text="commit?.hash.slice(0, 7)"
        :text-to-copy="commit?.hash"
      ></gf-copy-commit-hash>
    </div>
    <gf-custom-modal-window
      v-if="commit?.verificationResult"
      :gf-modal-data-name="'commitGpgInfoModalWindow-' + commit?.hash"
      :gf-modal-header="$t('commit.gpg.title')"
      gf-modal-wrapper-width="500px"
    >
      <template #body>
        <div>
          <p>
            {{ $t('commit.gpg.status') }}:
            {{ gpgKeyInfo.text }}
          </p>
          <p v-if="commit?.verificationResult?.comment">
            {{ $t('commit.gpg.reason.title') }}:
            <span v-if="commit?.verificationResult?.comment === 'no.public.key.found'">
              {{ $t('commit.gpg.reason.noPublicKeyFound') }}
            </span>
            <span v-else-if="commit?.verificationResult?.comment === 'key.expired'">
              {{ $t('commit.gpg.reason.keyExpired') }}
            </span>
            <span
              v-else-if="
                commit?.verificationResult?.comment === 'commiter.signature.emails.not.match'
              "
            >
              {{ $t('commit.gpg.reason.commiterSignaatureEmailsNotMatch') }}
            </span>
            <span v-else-if="commit?.verificationResult?.comment === 'other'">
              {{ $t('commit.gpg.reason.other') }}
            </span>
          </p>
          <p v-if="commit?.verificationResult?.keyId">
            {{ $t('commit.gpg.id') }}: {{ commit?.verificationResult?.keyId }}
          </p>
        </div>
      </template>
    </gf-custom-modal-window>

    <gf-custom-modal-window
      v-if="!isTagsDisplaying"
      :ref="`gfModalWindowRevertCommit-${commit.hash}`"
      :gf-modal-data-name="`gfModalWindowRevertCommit-${commit.hash}`"
      gf-modal-header="Отменить коммит"
      gf-modal-wrapper-width="500px"
      @close="onClose"
    >
      <template #body>
        <project-commit-revert-form
          @cancel="closeRevertCommitModal"
          @submit="revertCommit"
          :hash="commit.hash"
        />
      </template>
    </gf-custom-modal-window>
  </section>
</template>

<script>
import ACTION_ENUM from '@shared/modules/custom-roles/custom-roles.constants';
import commitService from '@/shared-modules/modules/commit/commit.service';
import ProjectCommitRevertForm from '@/shared-modules/modules/commit/components/project-commit-revert-form.vue';
import ciCdService from '@/shared-modules/modules/ci-cd/ci-cd.service';
import { FORM_NAME_ENUMS } from '@/shared-modules/variables/form-name.enums';
import commitMessages from '@/shared-modules/modules/commit/commit.messages';
import GfCustomTooltip from '@/shared-modules/shared-components/shared/GfCustomTooltip.vue';
export default {
  name: 'CommitListItem',
  components: { ProjectCommitRevertForm, GfCustomTooltip },
  props: {
    commit: {
      type: Object,
      default: null
    },
    hasReactiveLinks: {
      type: Boolean,
      default: true
    },
    canCreateTag: {
      type: Boolean,
      default: false
    },
    canStartContainer: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ACTION_ENUM() {
      return ACTION_ENUM;
    },
    alias() {
      return this.$route?.params?.alias;
    },
    projectAlias() {
      return this.$route?.params?.projectAlias;
    },
    currentBranch() {
      return this.$route?.query?.branch;
    },
    isTagsDisplaying() {
      return !!this.$route?.query?.tag;
    },
    gpgKeyInfo() {
      return this.commit.verificationResult.expired
        ? {
            elemStyle:
              'color: var(--gpg-key-expired-color); border: 1px solid var(--gpg-key-expired-color)',
            icon: 'gf-icon-history',
            text: this.$t('commit.gpg.expired')
          }
        : this.commit.verificationResult.verified
        ? {
            elemStyle: 'color: var(--success); border: 1px solid var(--success);',
            icon: 'gf-icon-check',
            text: this.$t('commit.gpg.verified')
          }
        : {
            elemStyle: 'color: var(--orange); border: 1px solid var(--orange);',
            icon: 'gf-icon-alert',
            text: this.$t('commit.gpg.unverified')
          };
    }
  },
  methods: {
    getActionAvailabilityFlag(action) {
      return this.$store.getters.getActionAvailabilityFlag(action);
    },
    openRevertCommitModal() {
      this.$refs[`gfModalWindowRevertCommit-${this.commit.hash}`].openModalWindow();
    },
    closeRevertCommitModal() {
      this.$refs[`gfModalWindowRevertCommit-${this.commit.hash}`].closeModalWindow();
    },
    revertCommit(emitedData) {
      let dataToSubmit = {
        currentBranch: this.currentBranch,
        newBranch: emitedData.newBranchName || null,
        commitHash: this.commit.hash,
        projectAlias: this.projectAlias,
        ownerAlias: this.alias,
        createMergeRequest: emitedData.initNewMergeRequest
      };

      if (emitedData.initNewMergeRequest) {
        commitService
          .revertCommitWithNewMergeRequest(
            this.alias,
            this.projectAlias,
            this.commit.hash,
            dataToSubmit
          )
          .then((data) => {
            window.location.href = `/project/${this.alias}/${this.projectAlias}/merge-request/create?compare=${data.sourceBranch}&base=${data.targetBranch}&sourceProjectId=${data.projectUuid}&commitToRevert=${data.commitToRevertHash}&commitMessage=${data.shortMessage}&isRevert=true`;
          })
          .catch((err) => {
            throw err;
          });
      } else {
        commitService
          .revertCommit(this.alias, this.projectAlias, this.commit.hash, dataToSubmit)
          .then((data) => {
            if (data) {
              this.$emit('revert');
              this.closeRevertCommitModal();
            } else {
              return;
            }
          })
          .catch((err) => {
            if (err.response.status == 403) {
              this.$store.dispatch('setFormErrors', {
                formName: FORM_NAME_ENUMS.COMMIT_REVERT_FORM,
                errorResponse: [
                  {
                    field: 'noPermission',
                    message: this.$t('commit.revert.no_permission')
                  }
                ]
              });
            }
          });
      }
    },
    startCommitPipeline(commitHash) {
      ciCdService.startCommitPipeline(this.alias, this.projectAlias, commitHash);
    },
    onClose() {
      this.$store.dispatch('setFormErrors', {
        formName: FORM_NAME_ENUMS.COMMIT_REVERT_FORM,
        errorResponse: null
      });
    }
  },
  i18n: {
    messages: commitMessages
  }
};
</script>

<style lang="scss">
$this: '.commit-list-item';
#{$this} {
  & {
    display: flex;
    justify-content: space-between;
    width: 100%;

    &__info {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      width: 70%;

      & .commit-message {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin-bottom: 0.25rem;
        font-weight: 550;

        &__content {
          color: var(--text) !important;
        }
      }

      & .commit-user {
        display: flex;
        align-items: center;
      }
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }
}
</style>
